/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * Surface component overrides for the Renaissance design system.
 * Covers MuiCssBaseline, MuiCard, MuiPaper, MuiDialog, MuiAlert, MuiMenu,
 * MuiPopover, MuiTooltip.
 *
 * Radii come from the concentric ladder (panel 28 · card 16 · row 10 · pill)
 * and shadows from the `depth(mode)` ladder — plan task 1.1's port of the
 * approved canvas overrides. MuiPaper's own radius/elevation are left at
 * MUI's defaults on purpose: in the portal those Papers are the shell's glass
 * surfaces (`UIPage`, `Sidebar`, `UIListDetailView`), which Group 4 migrates.
 */

import { glassMaterial, glassMaterialLevel } from '../glass'
import { glass } from '../hyper'
import {
  density,
  depth,
  numeric,
  radii,
  radius,
  surface,
  type TDensity,
} from '../tokens'

export function surfaceOverrides(
  mode: 'dark' | 'light',
  densityKey: TDensity = 'regular'
) {
  const d = density[densityKey]
  return {
    MuiCssBaseline: {
      styleOverrides: {
        // Dense financial tables are the primary surface: lining, tabular
        // figures document-wide so a stray <span> in a money column cannot
        // fall back to proportional digits.
        'table, [data-numeric]': {
          fontVariantNumeric: numeric.fontVariantNumeric,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          width: '100%',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          backgroundColor:
            mode === 'dark'
              ? 'rgba(255,255,255,0.055)'
              : 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${surface[mode].separator}`,
          boxShadow:
            mode === 'dark'
              ? '0 1px 2px rgba(0,0,0,0.5)'
              : '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(10,30,45,0.10)',
        },
      },
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: 'glass' as const },
          style: glassMaterial(mode),
        },
        {
          props: { variant: 'glass1' as const },
          style: glassMaterialLevel(mode, 1),
        },
        {
          props: { variant: 'glass2' as const },
          style: glassMaterialLevel(mode, 2),
        },
        {
          props: { variant: 'glass3' as const },
          style: glassMaterialLevel(mode, 3),
        },
        {
          props: { variant: 'glass4' as const },
          style: glassMaterialLevel(mode, 4),
        },
        {
          props: { variant: 'glass5' as const },
          style: glassMaterialLevel(mode, 5),
        },
      ],
    },
    MuiMenu: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 0,
          },
        },
      },
    },
    // Menus, selects (`MuiSelect` renders through `MuiPopover`) and every
    // other Popover are chrome: the sheet-level glass material. Promoted
    // (plan task 1.11) from a hand-copied set of the same numbers to the
    // shared `glass(mode, 'sheet')` recipe — chrome only, never content; see
    // `Theme/hyper.ts`'s `glass()` for the full rule and the D6 `@supports`
    // fallback every level below carries.
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: radius.card,
          ...glass(mode, 'sheet'),
        },
      },
    },
    // An `Autocomplete`'s popup (the select family's type-ahead tier,
    // `UIInputSelectFetch`) is the same menu as a plain `Select`'s: the
    // popover's rounded glass sheet, and option rows with `MuiMenuItem`'s
    // geometry at regular weight — one menu look for every select kind.
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: radius.card,
          ...glass(mode, 'sheet'),
        },
        listbox: {
          padding: 4,
          '& .MuiAutocomplete-option': {
            minHeight: d.rowHeight,
            borderRadius: radius.row,
            fontWeight: 400,
          },
        },
      },
    },
    // A sheet is a floating panel: panel radius, overlay depth, solid panel
    // surface. Glass belongs to its footer (chrome), never its body.
    MuiDialog: {
      defaultProps: {
        slotProps: {
          backdrop: {
            sx: {
              backgroundColor:
                mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.5)',
            },
          },
          paper: {
            elevation: 0,
          },
        },
      },
      styleOverrides: {
        paper: {
          borderRadius: radius.panel,
          backgroundColor: surface[mode].panel,
          backgroundImage: 'none' as const,
          boxShadow: depth(mode).overlay,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: radii.xs, fontSize: d.fontSize - 1 },
      },
    },
  }
}
